import { PAX_TYPE } from '@app/enums';
import { IROPS_STATUS } from '@app/enums/irops.enum';
import moment from 'moment';

const SEGMENT_STATUS = ['SC', 'WK', 'EXCH'];
const IROPS_CONFIRMATION_ACTIONS = ['SCH2HK', 'CONFIT'];

/**
 * Busca una accion de confirmacion IROPS (SCH2HK o CONFIT) dentro de una coleccion de items,
 * inspeccionando el campo indicado. Robusta ante colecciones nulas/ausentes y campos
 * indefinidos.
 *
 * CI-23582: la coincidencia es por subcadena, sin anclaje. No se puede usar startsWith porque
 * el freeText de un OSI viene prefijado con el airlineCode ("YY CONFIT INV KIO ..."), asi que
 * anclar al inicio dejaria de detectar todas las confirmaciones asentadas como OSI.
 */
function containsConfirmationAction(items: any[], field: 'freeText' | 'text'): boolean {
  return (items || []).some((item) =>
    IROPS_CONFIRMATION_ACTIONS.some((action) =>
      (item?.[field] || '').toUpperCase().includes(action)
    )
  );
}

/**
 * Verifica si la reservación tiene un remark de confirmación de IROPs (SCH2HK o CONFIT).
 *
 * CI-23582: revisa las dos colecciones del PNR, cada una con su campo correspondiente:
 * - genericSpecialRequests[].freeText -> OSI. Donde cae el CONFIT antes del fix, los PNR
 *   historicos, y el CONFIT de "Vuelos alternativos" que sigue siendo OSI a proposito.
 * - remarks[].text -> Remark de texto libre. Donde cae el CONFIT de aceptacion tras el fix, y
 *   tambien los SCH2HK que escribe el canal app (deteccion cross-canal).
 *
 * No es redundancia: cubre el antes y el despues del fix de forma simultanea.
 * `remarks` no tiene fallback a [] en el modelo Reservation, por eso las guardas del helper.
 */
function hasIropsConfirmationRemark(pnr: any): boolean {
  return (
    containsConfirmationAction(pnr?.genericSpecialRequests, 'freeText') ||
    containsConfirmationAction(pnr?.remarks, 'text')
  );
}

function validateIropsFlow(pnr: any, location?: string, hasConfirmedIrops = false): Boolean {
  const arrayCollection = pnr?.flightSummary?.segments ?? [];
  const currentSegment = pnr?.flightSummary?.currentSegment;
  let arrayAfects = [];
  let isElegible = false;

  if (hasConfirmedIrops) {
    return false;
  }

  if (hasIropsConfirmationRemark(pnr)) {
    return false;
  }

  const currentSegmentIndex = currentSegment
    ? arrayCollection.findIndex((s: any) => s.segmentCode === currentSegment.segmentCode)
    : 0;

  const fromIndex = currentSegmentIndex >= 0 ? currentSegmentIndex : 0;

  arrayAfects = arrayCollection.filter(
    (seg: any, index: number) =>
      (seg.isChangeOag || seg.isCancelOag || SEGMENT_STATUS.includes(seg.segmentStatus)) &&
      index >= fromIndex
  );

  isElegible = arrayAfects.length > 0;

  return isElegible;
}

function validateIropsBanner(segments: Array<any>): boolean {
  return !!segments.find((seg) => {
    const { flightsNewInfo, iropEligibility } = seg;
    let exist = false;

    if (iropEligibility) {
      exist =
        iropEligibility?.iropAffected &&
        iropEligibility?.elegibilityToExchange &&
        flightsNewInfo?.status === IROPS_STATUS.SCHEDULED;
    }

    return exist;
  });
}

/* End: Validate Functions */

/* Parse Functions */

function getTime(dateTime): String {
  let time = '';

  if (dateTime != null) {
    let timeIndex = dateTime.indexOf('T');
    if (timeIndex != -1) {
      timeIndex++;
    }

    time = dateTime.substr(timeIndex, 5);
  }

  return time;
}

function calculateTimeFlight(departureOrigin, departureNew) {
  let newDate = moment(departureNew);
  let oldDate = moment(departureOrigin);

  let diferences = newDate.diff(oldDate, 'hours', true);
  let hours = Math.floor(diferences);

  const minutes = Math.round((diferences - hours) * 60);
  const time = moment().startOf('day').add(hours, 'hours').add(minutes, 'minutes');

  let hourDiff = time.hours();
  let minutesDiff = time.minutes();

  return {
    hours: hourDiff,
    minutes: minutesDiff,
    status: hourDiff > 0 || minutesDiff > 0 ? 'DELAYED' : 'AHEAD',
  };
}

function calculateTimeFlightwithType(departureOrigin, departureNew, status) {
  const newDate = moment(departureNew);
  const oldDate = moment(departureOrigin);
  const diferences = newDate.diff(oldDate, 'minutes');
  const absDifferences = Math.abs(diferences);

  const hours = Math.floor(absDifferences / 60);
  const minutes = absDifferences % 60;
  const finalStatus = status === IROPS_STATUS.DELAYED ? 'DELAYED' : 'AHEAD';

  return {
    hours,
    minutes,
    status: finalStatus,
  };
}

function convertMinutes(minutes) {
  const hours = Math.floor(minutes / 60);
  const restMinutes = minutes % 60;
  return `${hours}h ${restMinutes}m`;
}

function parseTime(departureDateTime, arrivalDateTime) {
  const separateDep = departureDateTime?.includes('T') ? 'T' : ' ';
  const separateArr = arrivalDateTime?.includes('T') ? 'T' : ' ';
  const splitDeparture = departureDateTime?.split(separateDep);
  const splitArrival = arrivalDateTime?.split(separateArr);

  const hourDeparture = splitDeparture?.[1]?.split(':') || '';
  const hourArrival = splitArrival?.[1]?.split(':') || '';

  return {
    departureTime: `${hourDeparture[0]}:${hourDeparture[1]}`,
    arrivalTime: `${hourArrival[0]}:${hourArrival[1]}`,
  };
}

function verifyChildAlone(passengers) {
  const filter = passengers.find((p) => p.paxType === PAX_TYPE.ADULT);
  return filter ? false : true;
}

function getSegmentDepartureDate(segment) {
  if (
    checkSegmentIsAffected(segment) &&
    (segment?.flightsNewInfo?.status == IROPS_STATUS.DELAYED ||
      segment?.flightsNewInfo?.status == IROPS_STATUS.EARLY)
  ) {
    return segment?.flightsNewInfo?.departureDateTime;
  }

  return segment?.departureDateTime;
}

function getSegmentArrivalDate(segment) {
  if (
    checkSegmentIsAffected(segment) &&
    (segment?.flightsNewInfo?.status == IROPS_STATUS.DELAYED ||
      segment?.flightsNewInfo?.status == IROPS_STATUS.EARLY)
  ) {
    return segment?.flightsNewInfo?.arrivalDateTime;
  }

  return segment?.arrivalDateTime;
}

/* End: Parse Functions */

/* General Functions */

function checkSegmentIsAffected(segment) {
  return segment && segment?.flightsNewInfo;
}

function getFlightStatus(
  proposedDate: string,
  originalDate: string
): {
  hours: number;
  minutes: number;
  status: 'Delayed' | 'Early';
} {
  const newDate = new Date(proposedDate);
  const original = new Date(originalDate);

  const diffMs = newDate.getTime() - original.getTime();
  const diffMinutes = Math.floor(diffMs / 60000);
  const absMinutes = Math.abs(diffMinutes);
  const hours = Math.floor(absMinutes / 60);
  const minutes = absMinutes % 60;

  const status = diffMinutes > 5 ? 'Delayed' : 'Early';

  return {
    hours,
    minutes,
    status,
  };
}
/* End: General Functions */

/* Two Segment Disclaimer Logic */

export interface TwoSegmentDisclaimerInput {
  segments: Array<{
    isChangeOag?: boolean;
    isCancelOag?: boolean;
    segmentStatus?: string;
    segmentCode?: string;
    windowSegmentStatus?: string;
  }>;
  currentSegment: {
    segmentCode: string;
  };
  confirmedSegmentCodes: string[];
}

/**
 * Determina si se debe mostrar el disclaimer IROPS para itinerarios de 2 segmentos.
 *
 * - Si segments.length !== 2, delega a validateIropsFlow existente.
 * - Determina cuáles segmentos están afectados (isChangeOag, isCancelOag, o SEGMENT_STATUS).
 * - Para cada segmento afectado, verifica si el pasajero ya está confirmado.
 * - Retorna true si al menos un segmento afectado NO está confirmado.
 * - Retorna false si TODOS los segmentos afectados ya están confirmados.
 */
function shouldShowIropsDisclaimer2Segments(input: TwoSegmentDisclaimerInput): boolean {
  const { segments, currentSegment, confirmedSegmentCodes } = input;

  if (!segments || segments.length !== 2) {
    const pnr = {
      flightSummary: {
        segments,
        currentSegment,
      },
    };
    return !!validateIropsFlow(pnr);
  }

  const affectedSegments = segments.filter(
    (seg) => seg.isChangeOag || seg.isCancelOag || SEGMENT_STATUS.includes(seg.segmentStatus)
  );

  if (affectedSegments.length === 0) {
    return false;
  }

  const allAffectedConfirmed = affectedSegments.every(
    (seg) => seg.segmentCode && confirmedSegmentCodes.includes(seg.segmentCode)
  );

  return !allAffectedConfirmed;
}

/* End: Two Segment Disclaimer Logic */

export {
  getFlightStatus,
  validateIropsFlow,
  validateIropsBanner,
  getTime,
  calculateTimeFlight,
  calculateTimeFlightwithType,
  convertMinutes,
  parseTime,
  verifyChildAlone,
  getSegmentDepartureDate,
  getSegmentArrivalDate,
  checkSegmentIsAffected,
  hasIropsConfirmationRemark,
  shouldShowIropsDisclaimer2Segments,
};
